/* global GM_STATE */
import { useEffect, useState } from 'preact/hooks'
import { useOmneoClient } from './useOmneoClient'

/**
 * The customer's spendable Fit Club reward balance, in cents.
 *
 * Omneo reports the balance in dollars; every consumer wants cents, so the conversion happens once
 * here. Returns 0 for a logged-out customer and until the first response lands, so a caller can
 * treat "no balance yet" and "no balance" the same way — neither should render a reward line.
 *
 * The two-second delay is carried over from the call sites this replaces: a reward earned on the
 * order that just completed is not on the profile the instant the page loads. `omneo:refresh-balance`
 * is the event the account components dispatch after anything that can move the balance.
 *
 * @param {boolean} [active] Pass false to hold off until the surface is actually shown — the mini
 *   cart is in the DOM on every page but closed, and waking Omneo for it would be wasted work.
 * @returns {number} Balance in cents.
 */
export function useRewardBalance (active = true) {
  const { client } = useOmneoClient()
  const [balance, setBalance] = useState(0)

  useEffect(() => {
    if (!active || !client?.client) return

    let cancelled = false
    let timer = null

    const read = () => {
      client.profileClient.getBalance().then((data) => {
        if (cancelled || !data || typeof data.reward_balance !== 'number') return
        setBalance(data.reward_balance * 100)
      })
    }

    const refresh = () => {
      clearTimeout(timer)
      timer = setTimeout(read, 2000)
    }

    refresh()
    window.addEventListener('omneo:refresh-balance', refresh)

    return () => {
      cancelled = true
      clearTimeout(timer)
      window.removeEventListener('omneo:refresh-balance', refresh)
    }
  }, [client, active])

  return GM_STATE?.customer?.logged ? balance : 0
}
